import React, { useEffect, useState } from 'react';
import { t } from '../../i18n';
import { useCurrency } from '../../utils/currencyContext';

// Busted-player action slot: ask for a rebuy, wait on the host, or a thin
// spectating pill. Shared by table and list so the two layouts cannot drift.

const CARD = 'py-3 border border-line rounded-[14px] px-4';

function AmountRebuyRow({
  amount, onAmountChange, onSubmit, submitting, disabled, testPrefix = 'rebuy-ask',
}) {
  const { symbol, amountInputProps } = useCurrency();
  return (
    <div className="flex items-center gap-2 mt-3">
      <label className="relative flex-1 min-w-0">
        <span
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] tnum text-ink pointer-events-none"
          aria-hidden="true"
        >
          {symbol}
        </span>
        <input
          type="text"
          {...amountInputProps}
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          disabled={submitting}
          data-testid={`${testPrefix}-amount`}
          aria-label={t('common.amount')}
          enterKeyHint="done"
          className="w-full h-12 rounded-[12px] border border-line bg-white text-ink text-[15px] tnum pl-7 pr-3 focus:border-ink focus:outline-none disabled:opacity-40"
        />
      </label>
      <button
        type="button"
        onClick={onSubmit}
        disabled={disabled || submitting}
        data-testid={`${testPrefix}-submit`}
        style={{ WebkitTapHighlightColor: 'transparent' }}
        className="h-12 px-5 rounded-[12px] bg-ink text-cream text-[15px] font-semibold tracking-[-0.005em] flex-shrink-0 disabled:bg-line disabled:text-mute"
      >
        {t('poker.rebuy')}
      </button>
    </div>
  );
}

function RebuyAskForm({
  defaultAmount, onRequest, onSpectate, submitting = false, showSpectate = true,
}) {
  const { parseCurrencyInput } = useCurrency();
  const [amount, setAmount] = useState(defaultAmount || '');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!touched && defaultAmount != null) setAmount(defaultAmount);
  }, [defaultAmount, touched]);

  let cents = 0;
  try {
    cents = parseCurrencyInput(amount);
  } catch {
    cents = 0;
  }
  const canSubmit = cents > 0 && !submitting;

  return (
    <div className={`${CARD} text-center`} data-testid="rebuy-ask">
      <p className="text-[14px] font-semibold text-ink m-0 leading-snug">
        {t('game.chiplessCanRebuy')}
      </p>
      <AmountRebuyRow
        amount={amount}
        onAmountChange={(value) => {
          setTouched(true);
          setAmount(value);
        }}
        onSubmit={() => canSubmit && onRequest?.(cents)}
        submitting={submitting}
        disabled={!canSubmit}
      />
      {showSpectate && (
        <button
          type="button"
          onClick={onSpectate}
          data-testid="rebuy-ask-spectate"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="mt-2.5 bg-transparent border-0 p-0 text-[13px] text-mute font-medium"
        >
          {t('game.spectate')}
        </button>
      )}
    </div>
  );
}

function RebuyWaitingCard({ amountLabel, onCancel, cancelling = false }) {
  return (
    <div className={`${CARD} text-center`} data-testid="rebuy-ask-waiting">
      <p className="text-[14px] font-semibold text-ink m-0">
        {t('game.rebuySent', { amount: amountLabel })}
      </p>
      <p className="text-[12.5px] text-mute mt-1 m-0">
        {t('game.waitingHostAdd')}
      </p>
      <button
        type="button"
        onClick={onCancel}
        disabled={cancelling}
        data-testid="rebuy-ask-cancel"
        style={{ WebkitTapHighlightColor: 'transparent' }}
        className="mt-2.5 bg-transparent border-0 p-0 text-[13px] text-mute font-medium disabled:opacity-40"
      >
        {t('common.cancel')}
      </button>
    </div>
  );
}

function SpectatingPill({ onRebuy }) {
  return (
    <div className="flex justify-center" data-testid="spectating-pill">
      <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-line bg-cream">
        <span className="text-[13px] text-mute font-medium">{t('game.spectating')}</span>
        <span className="text-[13px] text-mute" aria-hidden="true">·</span>
        <button
          type="button"
          onClick={onRebuy}
          data-testid="spectating-rebuy"
          style={{ WebkitTapHighlightColor: 'transparent' }}
          className="bg-transparent border-0 p-0 text-[13px] font-semibold text-ink"
        >
          {t('poker.rebuy')}
        </button>
      </div>
    </div>
  );
}

function PendingRebuyRows({ requests = [], onAdd, addingId = null }) {
  const { formatCurrency } = useCurrency();
  if (!requests.length) return null;
  return (
    <div className="space-y-2" data-testid="pending-rebuys">
      {requests.map((req) => (
        <div
          key={req.playerId}
          data-testid={`pending-rebuy-${req.name}`}
          className="flex items-center gap-3 border border-line rounded-[14px] px-3.5 py-2.5"
        >
          <div className="min-w-0 flex-1 text-left">
            <div className="text-[14px] font-semibold text-ink truncate leading-tight">{req.name}</div>
            <div className="text-[12.5px] text-mute leading-tight mt-0.5">
              {t('game.wantsRebuy', { amount: formatCurrency(req.amount) })}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onAdd?.(req.playerId)}
            disabled={addingId === req.playerId}
            data-testid={`pending-rebuy-add-${req.name}`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
            className="h-9 px-4 rounded-[10px] bg-ink text-cream text-[13px] font-semibold flex-shrink-0 disabled:bg-line disabled:text-mute"
          >
            {addingId === req.playerId ? t('game.adding') : t('game.add')}
          </button>
        </div>
      ))}
    </div>
  );
}

export { RebuyAskForm, RebuyWaitingCard, SpectatingPill, PendingRebuyRows };
